import { PageLoadingIndicator } from 'components/loading-indicator'
import LocationMap from 'components/modal/location-map'
import { MultipleSelector } from 'components/multiple-selector'
import { NoResults, ValidationsErrors } from 'components/notifications'
import { PageHeaderWrapper, PageTitle, PageWrapper, PageWrapperBtn } from 'components/page'
import PageSearch from 'components/page/page-search'
import PageWrapperDataFilter from 'components/page/page-wrapper-data-filter'
import Pagination from 'components/pagination'
import { TableWrapper, Td, Thead } from 'components/table'
import { withAPI } from 'hocs'
import { useDidMountEffect } from 'hooks'
import useLoadCompanies from 'hooks/api/useLoadCompanies'
import _ from 'lodash'
import { ClockInOut } from 'modules/clock-in-out'
import { useLoadClocks } from 'modules/clock-in-out/hooks'
import WithPermission from 'modules/with-permission'
import React, { useEffect, useState } from 'react'
import { getFormattedCompArr, getFullName } from 'utils'
import ReactDatePicker from 'react-datepicker';
import moment from 'moment'
import { getFormattedDateAPI, getDate } from 'components/dateHandle'

const columnList = ['Driver', 'Clock In', 'In Automatic', 'Clock Out', 'Out Automatic', 'Is Notified', 'Is Force Out', 'Location In', 'Location Out'];

const Clocks = ({ httpRequest }) => {
  const [search, setSearch] = useState('');
  const [from, setFrom] = useState(moment().subtract(30, 'd'));
  const [to, setTo] = useState(moment());
  const [currentPage, setCurrentPage] = useState(1);
  const { data: companies } = useLoadCompanies(httpRequest);
  const [selectedCompanies, setSelectedCompanies] = useState([]);
  const { data, isLoading, pagination, loadClocks } = useLoadClocks(httpRequest,
    { page: currentPage, companies: selectedCompanies, search, from: getFormattedDateAPI(from), to: getFormattedDateAPI(to) }
  );
  const [selectedLocation, setSelectedLocation] = useState({});
  const [isMapOpen, setIsMapOpen] = useState(false);
  const [error, setError] = useState({});

  const debouncedCompanySelect = _.debounce((company) => {
    setSelectedCompanies(company);
    setCurrentPage(1);
  }, 500)

  useDidMountEffect(() => {
    setSelectedCompanies(getFormattedCompArr(companies));
    return () => debouncedCompanySelect.cancel();
  }, [companies])

  const debouncedSearch = _.debounce((search) => {
    if (search.length !== 1) {
      setSearch(search);
    }
  }, 500);

  useEffect(() => document.title = `Clocks - ${process.env.REACT_APP_NAME}`)

  const handleFromChange = (date) => {
    setFrom(date);
    if (!moment(to).isBetween(moment(date), moment(date).add(1, 'Y'))) {
      setTo(moment(date).add(1, 'Y'));
    }
  }

  const handleToChange = (date) => {
    setTo(date);
    if (!moment(from).isBetween(moment(date).subtract(1, 'Y'), moment(date))) {
      setFrom(moment(date).subtract(1, 'Y'));
    }
  }

  return (
    <PageWrapper>
      <PageHeaderWrapper>
        <PageTitle title="Clocks" withFAQsPage />
        
        <WithPermission permissionsCheck={['CanDrive']} >
          <PageWrapperBtn>
            <ClockInOut setError={setError} refreshData={loadClocks} />
          </PageWrapperBtn>
        </WithPermission>

      </PageHeaderWrapper>
      <PageWrapperDataFilter>
        <PageSearch search={search} onChange={debouncedSearch} />
        {_.isArray(companies) && companies.length > 0 && (
        <div className="md:w-max w-full gap-2 flex md:flex-row flex-col">
            <ReactDatePicker
              selected={getDate(from)}
              maxDate={moment().toDate()}
              onChange={(date) => handleFromChange(date)}
              className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500
                            w-full sm:text-sm border-gray-300 rounded-md"
              placeholderText="from"
              disabled={isLoading}
            />
            <ReactDatePicker
              selected={getDate(to)}
              maxDate={moment().toDate()}
              onChange={(date) => handleToChange(date)}
              className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500
                        w-full sm:text-sm border-gray-300 rounded-md"
              placeholderText="to"
              disabled={isLoading}
            />
          </div>
        )}
        {_.isArray(companies) && companies.length > 1 && (
          <div className="md:w-max w-full" data-testid="companySelector">
            <MultipleSelector
              options={getFormattedCompArr(companies)}
              defaultSelectedOptions={getFormattedCompArr(companies)}
              onChangeSelectedOptions={debouncedCompanySelect}
            />
          </div>
        )}
      </PageWrapperDataFilter>
      <LocationMap
        open={isMapOpen}
        setOpen={() => { setIsMapOpen(false); setSelectedLocation({ lat: '', lng: '' }); }}
        location={[ selectedLocation?.lat, selectedLocation?.lng ]}
      />
      <TableWrapper>
        {!_.isEmpty(error) && (
          <ValidationsErrors errors={error} />
        )}

        {data?.length === 0 && selectedCompanies?.length > 0 && !isLoading && (<NoResults />)}
        {selectedCompanies?.length === 0 && (<p className="text-sm">Select company</p>)}
        <PageLoadingIndicator loading={isLoading} />
        <table className="divide-y divide-gray-200 overflow-x-hidden overflow-y-visible w-full">
          {data?.length > 0 && (
            <Thead columnList={columnList} />
          )}
          <tbody className={`w-full divide-y ${isLoading ? 'opacity-50' : ''}`}>
            {data?.length > 0 && data.map((item) => (
              <tr key={item?.id} className="text-sm">
                <Td>{`${getFullName(item?.user)} ${item?.user?.driver?.driver_no || ''}`}</Td>
                <Td>{item?.in_at}</Td>
                <Td>{item?.truck_check_in ? "Yes" : "No"}</Td>
                <Td>{item?.out_at}</Td>
                <Td>{item?.truck_check_out ? "Yes" : "No"}</Td>
                <Td>{item?.is_notified ? "Yes" : "No"}</Td>
                <Td>{item?.is_automatic ? "Yes" : "No"}</Td>
                <Td
                  as="btnView"
                  condition={item?.in_lat !== null && item?.in_lng !== null}
                  onClick={() => {setSelectedLocation({ lat: item?.in_lat, lng: item?.in_lng}); setIsMapOpen(true)}}
                />
                <Td
                  as="btnView"
                  condition={item?.out_lat !== null && item?.out_lng !== null}
                  onClick={() => {setSelectedLocation({ lat: item?.out_lat, lng: item?.out_lng}); setIsMapOpen(true)}}
                />
              </tr>
            ))}
          </tbody>
        </table>
        <Pagination
          {...pagination}
          currentPage={currentPage}
          onChange={(page) => setCurrentPage(page)}
        />
      </TableWrapper>
    </PageWrapper>
  )
}

export default _.flowRight([withAPI])(Clocks);